/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Button,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Grid,
  Collapse,
  IconButton,
} from "@mui/material";
import Split from "react-split";
import Editor from "@monaco-editor/react";
import { useMsal } from "@azure/msal-react";
import { useLocation } from "react-router-dom";
import { CommentSection } from "react-comments-section";
import { ApiClient } from "../helpers/apiClient";
import { logger } from "../helpers/logger";
import { FaComments, FaArrowCircleRight } from "react-icons/fa";
import { MdExpandMore, MdExpandLess } from "react-icons/md";
import "react-comments-section/dist/index.css";
import { ParticipantInEventDTO } from "../types/Exercise";
import { LanguageType, ThemeType } from "../utils/exerciseUtils";
import { Comment } from "../types/api";
import { useProfileContext } from "../contexts/ProfileContext";
import GradingPanel from "./GradingPanel";
import {
  ParticipantGradingDTO,
  ParticipantGradingRemarkDTO,
} from "../types/ExerciseGrading";
import MergePanel from "./MergePanel";
import { MergeModeEnum } from "../enums/MergeModeEnum";
import { EventPhase } from "../enums/EventsPhaseEnum";
import InterviewPanel from "./InterviewPanel";
import InterviewSummary from "./InterviewSummary";

const LOGIN_URL = process.env.REACT_APP_ENDPOINT || "https://localhost:3000";

const FONT_STYLE = { fontFamily: "Verdana", fontSize: "13px" };

const ReadOnlyEditorPage: React.FC = () => {
  const { instance, accounts } = useMsal();
  const location = useLocation();
  const { profile } = useProfileContext();
  const [participantData, setParticipantData] =
    useState<ParticipantInEventDTO | null>(null);
  const [comments, setComments] = useState<any>([]);
  const [results, setResults] = useState<ParticipantGradingDTO[]>([]);
  const [remarks, setRemarks] = useState<ParticipantGradingRemarkDTO[]>([]);
  const [allRemarks, setAllRemarks] = useState<ParticipantGradingRemarkDTO[]>(
    [],
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [theme, setTheme] = useState<string>(
    localStorage.getItem("theme") ?? ThemeType["Visual Studio Dark"],
  );
  const [activeExerciseId, setActiveExerciseId] = useState<string>("");
  const [sliderValue, setSliderValue] = useState<number>(0);
  const [gradingOpen, setGradingOpen] = useState(true);

  const getLinkKeyFromQuery = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("linkKey");
  }, [location.search]);

  enum menuOptions {
    "Chat",
    "Logout",
  }
  const mergeMode = useMemo<MergeModeEnum>(() => {
    const merge = Number(new URLSearchParams(location.search).get("merge"));
    return merge in MergeModeEnum ? merge : MergeModeEnum.Grading;
  }, [location.search]);

  //const handleClose = () => setOpen(false);

  function handleClick(operation: menuOptions) {
    if (operation == menuOptions.Chat) {
      // do something
      setActiveExerciseId("chat");
    } else if (operation == menuOptions.Logout) {
      //do something else
      instance.logoutPopup();
    }
    // setOpen(!open);// to close the speed dial, remove this line if not needed.
  }

  const actions = [
    { icon: <FaComments />, name: "Chat", operation: menuOptions.Chat },
    {
      icon: <FaArrowCircleRight />,
      name: "Logout",
      operation: menuOptions.Logout,
    },
  ];

  const transformComments = useCallback((serverComments: Comment[]): any => {
    return (
      serverComments?.map((c) => ({
        comId: c.commId,
        userId: String(c.userId),
        comIdToDelete: c.commId,
        text: c.text,
        fullName: c.fullName,
        avatarUrl: `https://ui-avatars.com/api/name=${encodeURIComponent(
          c.fullName,
        )}&background=random`,
        timestamp: c.timeStamp,
        replies:
          c.replies?.map((r: any) => ({
            comId: r.commId,
            userId: String(r.userId),
            fullName: r.fullName,
            text: r.text,
            avatarUrl: `https://ui-avatars.com/api/name=${encodeURIComponent(
              r.fullName,
            )}&background=random`,
            timestamp: r.timeStamp,
            replies: [],
          })) || [],
      })) || []
    );
  }, []);

  const generateDefaultResults = useCallback(
    (
      participant: ParticipantInEventDTO | null,
      results: ParticipantGradingDTO[],
      phase: EventPhase,
    ): ParticipantGradingDTO[] => {
      if (!participant?.exercises?.length || profile === null) return results;

      const updatedResults = [...results];
      const existingIds = new Set(results.map((r) => r.gradingDetailsID));

      participant.exercises
        .filter((e) => e.phase === phase)
        .forEach((exercise) => {
          const gradingGroups = exercise.grading ?? [];

          gradingGroups.forEach((group) => {
            group.gradingDetails.forEach((detail) => {
              if (!existingIds.has(detail.id)) {
                updatedResults.push({
                  participantID: participant.id,
                  gradingDetailsID: detail.id,
                  gradingDetailsGroupID: group.id,
                  points: 0,
                  checked: false,
                  readonly: false,
                  correction: false,
                });
                existingIds.add(detail.id);
              }
            });
          });
        });

      return updatedResults;
    },
    [profile],
  );

  const fetchData = async () => {
    const apiClient = ApiClient.getInstance(instance, accounts[0], {
      baseUrl: process.env.REACT_APP_API_BASE_URL,
    });

    const linkKey = getLinkKeyFromQuery();
    if (!linkKey) {
      setError("Missing 'linkKey' parameter in URL");
      return;
    }

    try {
      setLoading(true);
      const participantResponse = await apiClient.get<ParticipantInEventDTO>(
        `Response/ParticipantResponses?linkKey=${encodeURIComponent(linkKey)}&merge=${mergeMode}`,
      );
      setParticipantData(participantResponse);
      document.title = participantResponse.fullName;
      const commentsResponse = await apiClient.get<Comment[]>(
        `Comment?participantID=${encodeURIComponent(participantResponse?.id)}`,
      );
      setComments(transformComments(commentsResponse));

      if (
        participantResponse &&
        participantResponse?.exercises &&
        participantResponse?.exercises?.length > 0
      ) {
        setActiveExerciseId(participantResponse.exercises[0].id);
      }

      let resultsResponse = await apiClient.get<ParticipantGradingDTO[]>(
        `Grading/Results/${encodeURIComponent(participantResponse?.id)}/${mergeMode}`,
      );

      if (mergeMode === MergeModeEnum.Grading) {
        resultsResponse = generateDefaultResults(
          participantResponse,
          resultsResponse,
          EventPhase.Test,
        );
      }

      if (mergeMode === MergeModeEnum.Interview) {
        resultsResponse = generateDefaultResults(
          participantResponse,
          resultsResponse,
          EventPhase.Interview,
        );
      }

      setResults(resultsResponse);

      const remarksResponse = await apiClient.get<
        ParticipantGradingRemarkDTO[]
      >(
        `Remark/CurrentTeamMember/${encodeURIComponent(participantResponse?.id)}`,
      );
      setRemarks(remarksResponse ?? []);

      if (mergeMode !== MergeModeEnum.Grading) {
        const allRemarksResponse = await apiClient.get<
          ParticipantGradingRemarkDTO[]
        >(
          `Remark/All/${encodeURIComponent(participantResponse?.id)}/${mergeMode === MergeModeEnum.Merge ? true : false}`,
        );
        setAllRemarks(allRemarksResponse ?? []);
      }
    } catch (err) {
      logger.error("Error fetching data:", err);
      setError("Failed to load participant data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (profile) {
      fetchData();
    }
  }, [profile]);

  useEffect(() => {
    const handleResizeObserverError = (e: ErrorEvent) => {
      if (
        e.message ===
        "ResizeObserver loop completed with undelivered notifications."
      ) {
        const resizeObserverErrDiv = document.getElementById(
          "webpack-dev-server-client-overlay-div",
        );
        const resizeObserverErr = document.getElementById(
          "webpack-dev-server-client-overlay",
        );
        if (resizeObserverErr) resizeObserverErr.style.display = "none";
        if (resizeObserverErrDiv) resizeObserverErrDiv.style.display = "none";
      }
    };

    window.addEventListener("error", handleResizeObserverError);
    return () => window.removeEventListener("error", handleResizeObserverError);
  }, []);

  const handleCommentAction = useCallback(
    async (action: "create" | "update" | "delete" | "reply", payload: any) => {
      const apiClient = ApiClient.getInstance(instance, accounts[0], {
        baseUrl: process.env.REACT_APP_API_BASE_URL,
      });
      if (!apiClient || !participantData) return;

      try {
        let response;
        switch (action) {
          case "create":
            response = await apiClient.post(
              "Comment",
              JSON.stringify({
                participantID: participantData.id,
                text: payload.text,
              }),
            );
            break;
          case "update":
            response = await apiClient.put(
              "Comment",
              JSON.stringify({
                commId: payload.comId,
                participantID: participantData.id,
                text: payload.text,
              }),
            );
            break;
          case "delete":
            response = await apiClient.delete<{ success: boolean }>(
              `Comment?commentId=${payload.comIdToDelete}`,
            );
            break;
          case "reply":
            response = await apiClient.post(
              "Comment",
              JSON.stringify({
                InReplyToCommentID: payload.repliedToCommentId,
                text: payload.text,
                participantID: participantData.id,
              }),
            );
            break;
        }

        const updatedComments = await apiClient.get<Comment[]>(
          `Comment?participantID=${participantData.id}`,
        );
        setComments(transformComments(updatedComments));
        return response;
      } catch (err) {
        logger.error(`Error performing comment ${action}:`, err);
        throw err;
      }
    },
    [participantData, transformComments],
  );

  const handleSaveRemarks = useCallback(
    async (groupId: number, remarkText: string) => {
      const apiClient = ApiClient.getInstance(instance, accounts[0], {
        baseUrl: process.env.REACT_APP_API_BASE_URL,
      });

      const payload = {
        participantID: participantData?.id,
        gradingDetailsGroupID: groupId,
        isMergeMode: mergeMode !== MergeModeEnum.Grading,
        remark: remarkText,
      };

      await apiClient.post("Remark", JSON.stringify(payload));

      setRemarks((prev) => {
        const existing = prev.find((r) => r.gradingDetailsGroupID === groupId);
        if (existing) {
          return prev.map((r) =>
            r.gradingDetailsGroupID === groupId
              ? { ...r, remark: remarkText }
              : r,
          );
        }
        return [
          ...prev,
          {
            gradingDetailsGroupID: groupId,
            remark: remarkText,
          } as ParticipantGradingRemarkDTO,
        ];
      });
    },
    [instance, accounts, participantData?.id, mergeMode],
  );

  const handleSendResults = useCallback(
    async (groupId: number) => {
      const apiClient = ApiClient.getInstance(instance, accounts[0], {
        baseUrl: process.env.REACT_APP_API_BASE_URL,
      });

      if (!apiClient || !participantData) return;

      try {
        const groupResults = results.filter(
          (r) => r.gradingDetailsGroupID === groupId,
        );

        let gradingsToSend = groupResults;

        if (mergeMode !== MergeModeEnum.Grading) {
          const filteredGroupResults = groupResults.filter(
            (r) => r.teamMember !== null,
          );

          const mergedResults = Object.values(
            filteredGroupResults.reduce<
              Record<number, (typeof groupResults)[0]>
            >((acc, r) => {
              if (!acc[r.gradingDetailsID]) {
                acc[r.gradingDetailsID] = { ...r, teamMember: null };
              }
              return acc;
            }, {}),
          );

          gradingsToSend = [...filteredGroupResults, ...mergedResults];
        }

        const payload = {
          participantID: participantData.id,
          gradingDetailsGroupID: groupId,
          mergeResults: mergeMode !== MergeModeEnum.Grading,
          gradings: gradingsToSend,
        };

        await apiClient.post("Grading/Results", JSON.stringify(payload));
      } catch (err) {
        logger.error("Error sending results:", err);
        throw err;
      }
    },
    [participantData, results, instance, accounts, mergeMode],
  );

  const handleSaveInterview = useCallback(
    async (remarksByGroup: Record<number, string>) => {
      const apiClient = ApiClient.getInstance(instance, accounts[0], {
        baseUrl: process.env.REACT_APP_API_BASE_URL,
      });

      if (!apiClient || !participantData) return;

      try {
        let gradingsToSend = results;

        const payload = {
          participantID: participantData.id,
          gradings: gradingsToSend,
        };

        await apiClient.post(
          "Grading/InterviewResults",
          JSON.stringify(payload),
        );
      } catch (err) {
        logger.error("Error sending results:", err);
        throw err;
      }

      try {
        for (const groupId in remarksByGroup) {
          const remarkText = remarksByGroup[groupId];
          handleSaveRemarks(Number(groupId), remarkText);
        }
      } catch (err) {
        logger.error("Error saving remarks:", err);
      }
    },
    [participantData, results, instance, accounts, mergeMode],
  );

  const exercises = useMemo(
    () => participantData?.exercises || [],
    [participantData],
  );
  const responseHistory = useMemo(
    () => participantData?.responseHistory || {},
    [participantData],
  );

  const allTabs = useMemo(
    () => [
      ...exercises.map((ex) => ({ id: ex.id, label: ex.title })),
      { id: "chat", label: "Notes" },
    ],
    [exercises],
  );

  const isChatTab = useMemo(
    () => activeExerciseId === "chat",
    [activeExerciseId],
  );
  const activeExercise = useMemo(
    () => exercises.find((ex) => ex.id === activeExerciseId),
    [exercises, activeExerciseId],
  );

  const historyEntries = useMemo(
    () => (activeExercise ? responseHistory[activeExercise.id] || [] : []),
    [activeExercise, responseHistory],
  );

  const selectedEntry = useMemo(
    () => historyEntries[sliderValue] || {},
    [historyEntries, sliderValue],
  );

  const code = useMemo(
    () => selectedEntry?.body || "// No submission found",
    [selectedEntry],
  );

  const language = useMemo(
    () => (selectedEntry?.language ?? "csharp").toLowerCase(),
    [selectedEntry],
  );

  useEffect(() => {
    if (historyEntries.length > 0) {
      setSliderValue(historyEntries.length - 1);
    } else {
      setSliderValue(0);
    }
  }, [activeExerciseId, historyEntries]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography variant="h6" sx={FONT_STYLE}>
          Loading participant data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
      >
        <Typography variant="h6" color="error" sx={FONT_STYLE}>
          {error}
        </Typography>
        <Button
          variant="contained"
          onClick={fetchData}
          style={{ marginTop: 16, fontFamily: "Verdana", fontSize: "13px" }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (!participantData) {
    return null;
  }

  return (
    <div style={{ width: "100%", overflow: "hidden" }}>
      <div
        style={{
          flex: 1,
          marginTop: 4,
          marginLeft: 30,
          marginRight: 30,
          position: "relative",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <div />
          <Typography
            variant="h6"
            style={{ fontFamily: "Verdana", fontSize: "13px" }}
          >
            {participantData.fullName}
          </Typography>
        </div>
        <Slider
          value={sliderValue}
          onChange={(_, newValue) => setSliderValue(newValue as number)}
          step={1}
          min={0}
          max={historyEntries.length - 1}
          valueLabelDisplay="auto"
          valueLabelFormat={(value) => {
            const dt = historyEntries[value]?.created;
            return dt ? dt.substring(0, 16) : "";
          }}
          marks={historyEntries.map((h, i) => ({
            value: i,
            label: h.submitted
              ? h.created.split(":").slice(0, 2).join(":")
              : "",
          }))}
          aria-label="Submission History"
          sx={{
            "& .MuiSlider-markLabel": FONT_STYLE,
            "& .MuiSlider-valueLabel": FONT_STYLE,
          }}
        />
      </div>

      <Split
        className="split"
        sizes={isChatTab ? [100, 0] : [40, 60]}
        minSize={[200, 0]}
        gutterSize={isChatTab ? 0 : 8}
        style={{ display: "flex", height: "calc(97vh - 44px)" }}
      >
        <div>
          <Box
            sx={{
              overflowY: "auto",
              borderRight: "1px solid #ccc",
              backgroundColor: "#fff",
              maxHeight: "99%",
            }}
          >
            {allTabs.length > 0 && (
              <Tabs
                value={activeExerciseId}
                onChange={(_, newValue) => {
                  setActiveExerciseId(newValue);
                }}
                variant="scrollable"
                scrollButtons="auto"
                orientation="horizontal"
                sx={{
                  borderBottom: "1px solid #ccc",
                  paddingX: "1rem",
                  "& .MuiTab-root": FONT_STYLE,
                }}
                aria-label="Exercise navigation tabs"
              >
                {allTabs.map((tab) => (
                  <Tab
                    key={tab.id}
                    label={tab.label}
                    value={tab.id}
                    sx={{ alignItems: "flex-start", ...FONT_STYLE }}
                  />
                ))}
              </Tabs>
            )}

            {!isChatTab && activeExercise && (
              <Box sx={{ padding: "1rem" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "0.5rem",
                  }}
                >
                  <Typography variant="h6" sx={FONT_STYLE}>
                    {activeExercise.title}
                  </Typography>

                  {activeExercise?.grading?.length > 0 && (
                    <IconButton
                      size="small"
                      onClick={() => setGradingOpen((prev) => !prev)}
                      aria-label="Toggle grading panel"
                    >
                      {gradingOpen ? <MdExpandLess /> : <MdExpandMore />}
                    </IconButton>
                  )}
                </Box>

                {mergeMode === MergeModeEnum.Grading && (
                  <Collapse in={gradingOpen} timeout="auto" unmountOnExit>
                    <Grid container spacing={2} sx={{ pl: 1 }}>
                      {activeExercise?.grading &&
                        activeExercise.grading.length > 0 && (
                          <GradingPanel
                            groups={activeExercise.grading}
                            readonly={participantData.finishedAt === null}
                            results={results}
                            remarks={remarks}
                            handleSendResults={handleSendResults}
                            onChange={setResults}
                            handleSaveRemarks={handleSaveRemarks}
                          />
                        )}
                    </Grid>
                  </Collapse>
                )}
                {(mergeMode === MergeModeEnum.Merge ||
                  mergeMode === MergeModeEnum.MergeResult) && (
                  <Collapse in={gradingOpen} timeout="auto" unmountOnExit>
                    <Grid container spacing={2} sx={{ pl: 1 }}>
                      {activeExercise?.grading &&
                        activeExercise.grading.length > 0 && (
                          <MergePanel
                            groups={activeExercise.grading}
                            results={results}
                            allRemarks={allRemarks}
                            handleSendResults={handleSendResults}
                            handleSaveRemarks={handleSaveRemarks}
                            onChange={setResults}
                          />
                        )}
                    </Grid>
                  </Collapse>
                )}

                {mergeMode === MergeModeEnum.Interview && (
                  <Collapse in={gradingOpen} timeout="auto" unmountOnExit>
                    <Grid container spacing={2} sx={{ pl: 1 }}>
                      {activeExercise?.grading &&
                        activeExercise.grading.length > 0 && (
                          <InterviewPanel
                            groups={activeExercise.grading}
                            phase={activeExercise.phase}
                            results={results}
                            allRemarks={allRemarks}
                            onChange={setResults}
                            handleSaveInterview={handleSaveInterview}
                          />
                        )}
                    </Grid>
                  </Collapse>
                )}

                <Typography
                  variant="body1"
                  sx={FONT_STYLE}
                  dangerouslySetInnerHTML={{ __html: activeExercise.body }}
                />
              </Box>
            )}

            {isChatTab && (
              <Box sx={{ padding: "1rem" }}>
                <Typography variant="body2" sx={FONT_STYLE}>
                  Below you can leave notes on this participant's submission.
                </Typography>
                <CommentSection
                  currentUser={{
                    currentUserId: profile?.id?.toString() || "",
                    currentUserFullName: profile?.fullName || "Unknown",
                    currentUserImg: `https://ui-avatars.com/api/name=${encodeURIComponent(
                      profile?.fullName || "Unknown",
                    )}&background=random`,
                    currentUserProfile: "",
                  }}
                  logIn={{
                    onLogin: () => alert("Call login function "),
                    signUpLink: LOGIN_URL,
                  }}
                  commentData={comments}
                  onSubmitAction={(data: any) =>
                    handleCommentAction("create", data)
                  }
                  onEditAction={(data: any) =>
                    handleCommentAction("update", data)
                  }
                  onDeleteAction={(data: any) =>
                    handleCommentAction("delete", data)
                  }
                  onReplyAction={(data: any) =>
                    handleCommentAction("reply", data)
                  }
                  advancedInput
                  replyInputStyle={{ border: "1px solid #ccc" }}
                  customNoComment={() => (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={FONT_STYLE}
                    >
                      No comments yet. Be the first to add one!
                    </Typography>
                  )}
                />
              </Box>
            )}
          </Box>
        </div>
        {activeExercise?.phase === EventPhase.Interview ? (
          <div>
            <InterviewSummary exercises={exercises} results={results} />
          </div>
        ) : (
          <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            {!isChatTab && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  backgroundColor: "#f1f1f1",
                  borderBottom: "1px solid #ccc",
                  padding: "0.5rem",
                }}
              >
                <FormControl
                  variant="outlined"
                  size="small"
                  style={{ marginRight: "1rem" }}
                  disabled
                >
                  <InputLabel sx={FONT_STYLE}>Language</InputLabel>
                  <Select
                    value={language}
                    label="Language"
                    style={{
                      minWidth: "100px",
                      fontFamily: "Verdana",
                      fontSize: "13px",
                    }}
                  >
                    {Object.keys(LanguageType).map((key) => (
                      <MenuItem
                        key={key}
                        value={LanguageType[key as keyof typeof LanguageType]}
                        sx={FONT_STYLE}
                      >
                        {key}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  variant="outlined"
                  size="small"
                  style={{ marginRight: "1rem" }}
                >
                  <InputLabel sx={FONT_STYLE}>Theme</InputLabel>
                  <Select
                    value={theme}
                    label="Theme"
                    onChange={(e) => {
                      setTheme(e.target.value as string);
                      localStorage.setItem("theme", e.target.value);
                    }}
                    style={{
                      minWidth: "100px",
                      fontFamily: "Verdana",
                      fontSize: "13px",
                    }}
                  >
                    {Object.keys(ThemeType).map((key) => (
                      <MenuItem
                        key={key}
                        value={ThemeType[key as keyof typeof ThemeType]}
                        sx={FONT_STYLE}
                      >
                        {key}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <SpeedDial
                  ariaLabel="SpeedDial"
                  sx={{ position: "absolute", bottom: 16, right: 16 }}
                  icon={<SpeedDialIcon />}
                >
                  {actions.map((action) => (
                    <SpeedDialAction
                      key={action.name}
                      icon={action.icon}
                      tooltipTitle={action.name}
                      onClick={() => {
                        handleClick(action.operation);
                      }}
                    />
                  ))}
                </SpeedDial>
              </div>
            )}

            <div
              style={{
                flex: 1,
                display: isChatTab ? "none" : "flex",
                flexDirection: "column",
              }}
            >
              <Editor
                height="98.5%"
                width="100%"
                language={language}
                theme={theme}
                value={code}
                options={{
                  readOnly: true,
                  minimap: { enabled: true },
                  contextmenu: false,
                  mouseWheelZoom: true,
                  quickSuggestions: true,
                  suggestOnTriggerCharacters: true,
                  parameterHints: { enabled: true },
                  autoClosingBrackets: "always",
                  formatOnType: true,
                  formatOnPaste: true,
                  autoIndent: "advanced",
                  dropIntoEditor: { enabled: false },
                }}
              />
            </div>
          </div>
        )}
      </Split>
    </div>
  );
};

export default ReadOnlyEditorPage;
